import CrossIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinerProgress from '@mui/material/LinearProgress';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import {
  ALL,
  MODULES,
  hasPermission as hasAllPermission,
} from 'components/Access';
import Button from 'components/Button/Button';
import DataTable from 'components/DataTable';
import PageWrapper from 'components/PageWrapper';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { importContacts } from 'services/contacts.service';
import { getGroups } from 'services/groups.service';
import toast from 'utils/toast';

class Import extends React.Component {
  state = {
    file: null,
    data: null,
    file_key: null,
    groups: [],
    groupOptions: [],
    headers: null,
    columns: null,
    hasPermission: false,
    firstRowHeader: true,
    allow_blank_values_wipe_data: false,
    mappings: {},
    results: [],
    showMapping: false,
    importing: false,
    complete: false,
  };

  componentDidMount() {
    getGroups().then((groups) => {
      this.setState({
        groupOptions: groups.map((g) => ({
          label: g.group_name,
          value: g.group_id,
        })),
      });
    });
  }

  onFileSelect = (e) => {
    const { t } = this.props;
    const file = e.target.files[0];
    if (file) {
      this.setState({
        file,
      });
    } else {
      toast.error(t('import-select-csv-error', 'Please select CSV file.'));
    }
  };

  onCheck = (e) => {
    const { name, checked } = e.target;
    this.setState({
      [name]: checked,
    });
  };

  onGroupSelect = (val) => {
    this.setState({
      groups: val,
    });
  };

  changeMapping = (name, value) => {
    this.setState({
      mappings: {
        ...this.state.mappings,
        [name]: value,
      },
    });
  };

  onSubmit = async () => {
    try {
      const resp = await importContacts({
        file: this.state.file,
      });
      const { columns, headers } = resp;
      const mappings = {};
      const mappedFields = {};
      headers.forEach((col) => {
        const lower = col.toLowerCase();
        let target = '';
        if (col === 'Contact Hash' || col === 'hash') {
          target = 'hash';
        } else if (
          col === 'First Name' ||
          lower === 'fname' ||
          lower.includes('first')
        ) {
          target = 'contact_first_name';
        } else if (
          col === 'Last Name' ||
          lower === 'lname' ||
          lower.includes('last')
        ) {
          target = 'contact_last_name';
        } else if (col === 'Email' || lower.includes('email')) {
          target = 'contact_email';
        } else if (
          col === 'Secondary Phone' ||
          lower === 'secondary phone' ||
          lower.includes('home')
        ) {
          target = 'secondary_phone';
        } else if (
          col === 'Primary Phone' ||
          lower === 'cell phone' ||
          lower.includes('mobile')
        ) {
          target = 'primary_phone';
        } else if (col === 'Address 1' || lower === 'address 1') {
          target = 'contact_address1';
        } else if (col === 'Address 2' || lower === 'address 2') {
          target = 'contact_address2';
        } else if (col === 'City' || lower.includes('city')) {
          target = 'contact_city';
        } else if (col === 'Zip' || lower.includes('zip')) {
          target = 'contact_zip';
        } else if (col === 'State' || lower.includes('state')) {
          target = 'contact_state';
        } else if (col === 'Country' || lower.includes('country')) {
          target = 'contact_country';
        } else if (col === 'Comments' || lower.includes('comment')) {
          target = 'contact_comments';
        } else {
          target = '';
        }
        if (!mappedFields[target]) {
          mappings[col] = target;
          mappedFields[target] = true;
        }
      });
      this.setState({ ...resp, mappings, showMapping: true });
    } catch (e) {
      toast.error(e.message);
    }
  };

  startImport = async () => {
    this.setState({ importing: true });
    try {
      const resp = await importContacts({
        file_key: this.state.file_key,
        headers_map: this.state.mappings,
        groups: this.state.groups,
        opt_in: this.state.hasPermission,
        allow_blank_values_wipe_data: this.state.allow_blank_values_wipe_data,
        action: 'import',
      });
      this.setState({ complete: true, results: resp });
    } catch (e) {
      toast.error(e.message);
    } finally {
      this.setState({ importing: false });
    }
  };

  renderMapFields = () => {
    const { t } = this.props;
    const { headers, mappings, columns, importing, complete } = this.state;

    if (importing || complete) return this.renderProgress();
    const fields = Object.values(mappings);
    const isValid =
      fields.includes('primary_phone') || fields.includes('contact_email');

    const { custom, contact } = Object.keys(columns || {}).reduce(
      (acc, key) => {
        const val = columns[key];
        const option = { label: val, value: key };
        if (key.startsWith('custom')) {
          acc.custom.push(option);
        } else {
          acc.contact.push(option);
        }
        return acc;
      },
      {
        custom: [],
        contact: [],
      },
    );

    const optionGroup = [
      { label: 'Standard fields', options: contact },
      {
        label: 'Custom fields',
        options: custom,
      },
    ];

    return (
      <Box>
        <Typography gutterBottom variant="h4" fontWeight={600}>
          {t('import-map-fields', 'Map The Fields')}
        </Typography>
        <Typography paragraph>
          {t(
            'import-map-fields-hint',
            'Using the columns from the first row, select the matching fields. Each field should only be used once.',
          )}
        </Typography>
        <table className="table">
          <thead>
            <tr>
              <th>{t('import-csv-file-first-row', 'CSV File First Row')}</th>
              <th>
                {t('import-tic-contact-field', 'Text In Church Contact Field')}
              </th>
            </tr>
          </thead>
          <tbody>
            {headers.map((col) => {
              const selected = mappings[col];
              return (
                <tr key={col}>
                  <td>{col}</td>
                  <td>
                    <Select
                      fullWidth
                      isClearable
                      transformValue
                      menuPosition="fixed"
                      margin="none"
                      value={selected}
                      name={col}
                      onChange={(val) => this.changeMapping(col, val)}
                      options={optionGroup}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!isValid && (
          <Alert severity="error">
            {t(
              'import-please-map-email-or-phone-error',
              'Please map an email or phone number field.',
            )}
          </Alert>
        )}
        <Box mt={2} textAlign="right">
          <Button
            variant="text"
            onClick={() => this.setState({ showMapping: false })}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Button onClick={this.startImport} disabled={!isValid}>
            {t('import-people', 'Import People')}
          </Button>
        </Box>
      </Box>
    );
  };

  renderProgress = () => {
    const { t } = this.props;
    const { results, complete, importing } = this.state;

    const columns = [
      { text: 'First Name', dataField: 'contact_first_name' },
      { text: 'Last Name', dataField: 'contact_last_name' },
      { text: 'Email', dataField: 'contact_email' },
      { text: 'Mobile', dataField: 'primary_phone' },
      {
        text: 'Success',
        dataField: '_successful',
        align: 'center',
        formatter: (val) =>
          val ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CrossIcon color="error" />
          ),
      },
      {
        text: 'Existing',
        dataField: '_matched_existing',
        align: 'center',
        formatter: (val) => (val ? 'Yes' : 'No'),
      },
      {
        text: 'Info',
        dataField: '_message',
      },
    ];

    return (
      <Box>
        <Typography paragraph variant="h4" fontWeight={600}>
          {t('import-people', 'Importing People')}
        </Typography>
        {importing && (
          <Alert severity="warning">
            <strong>Heads up!</strong> Please leave this window open while we're
            mapping each line from your .csv file!
          </Alert>
        )}
        {!complete && <LinerProgress variant="indeterminate" />}
        {complete && results.some((r) => r._successful) ? (
          <Box mt={3}>
            <Alert severity="success">
              Awesome! Your CSV import is complete!
            </Alert>
            <Box textAlign="right" mt={3}>
              <Button component={Link} to="/groups" sx={{ mr: 1 }}>
                Go To Groups
              </Button>
              <Button component={Link} to="/people">
                Go To People
              </Button>
            </Box>
          </Box>
        ) : (
          complete && (
            <Box mt={3}>
              <Button
                onClick={() => {
                  this.setState({
                    complete: false,
                    importing: false,
                    result: [],
                  });
                }}
              >
                Go Back
              </Button>
            </Box>
          )
        )}
        {complete && (
          <Paper sx={{ mt: 3 }}>
            <DataTable
              size="small"
              keyField="hash"
              data={results}
              columns={columns}
              pagination
            />
          </Paper>
        )}
      </Box>
    );
  };

  render() {
    const { t, user } = this.props;
    const {
      groups,
      groupOptions,
      file,
      showMapping,
      hasPermission,
      allow_blank_values_wipe_data,
    } = this.state;
    const hasAll = hasAllPermission(user, MODULES.GROUPS, ALL);

    return (
      <PageWrapper
        title="Upload a CSV"
        breadcrumbs={[{ title: 'Import Contacts' }]}
        sx={{
          '& .custom-file-input': {
            display: 'none',
          },
          '& code': {
            display: 'block',
            backgroundColor: 'background.paper',
            padding: '16px',
            margin: '10px 0',
            borderRadius: '8px',
          },
        }}
      >
        <Helmet title="Settings > Import" />
        <Paper variant="outlinedVariant" sx={{ p: 3 }}>
          {showMapping ? (
            this.renderMapFields()
          ) : (
            <Box>
              <Typography mb={3} variant="h4" fontWeight={600}>
                {t('import-how-to-import-csv', 'How To Upload A CSV')}
              </Typography>
              <Trans ns="settings" i18nKey="import-help-description">
                <p>
                  To upload contacts click the <b>Browse</b> button below and
                  find the CSV file on your computer, select a group to add the
                  contacts to, and then click the <b>Process File</b> button.
                </p>
                <p>
                  The first line of the CSV file should contain the names of the
                  columns for each of the remaining lines in the file. The
                  following columns are suggested:
                </p>
                <code>
                  Last Name, First Name, Email, Secondary Phone, Primary Phone,
                  Address 1, Address 2, City, State, Zip, Country, Comments
                </code>
                <p>
                  You can download the sample template and use it to import
                  contacts.
                </p>
              </Trans>
              <MuiLink href="/assets/downloads/Import-Template.csv">
                {t('import-download-template', 'Download CSV Template')}
              </MuiLink>
              <Card variant="containedVariant" sx={{ mt: 3 }}>
                <CardContent>
                  <Typography paragraph fontWeight={600}>
                    {t('import-upload-csv', 'Upload A CSV')}
                  </Typography>
                  <Box mb={3} display="flex" alignItems="center">
                    <Box mr={2}>
                      <div className="custom-file">
                        <input
                          accept=".csv,.xls,.xlsx"
                          type="file"
                          className="custom-file-input"
                          onChange={this.onFileSelect}
                          multiple={false}
                          id="csv-file"
                        />
                        <Button
                          color="secondary"
                          component="label"
                          htmlFor="csv-file"
                        >
                          {t('import-select-file', 'Choose file')}
                        </Button>
                      </div>
                    </Box>
                    <Typography>{file && file.name}</Typography>
                  </Box>
                  <Select
                    fullWidth
                    isMulti
                    transformValue
                    menuPosition="fixed"
                    label={t('import-select-groups', 'Select Group(s)')}
                    value={groups}
                    options={groupOptions}
                    onChange={this.onGroupSelect}
                    placeholder={t(
                      'import-select-group-placeholder',
                      'Click Here to Select a Group',
                    )}
                  />
                  <Switch
                    fullWidth
                    checked={allow_blank_values_wipe_data}
                    onChange={this.onCheck}
                    name="allow_blank_values_wipe_data"
                    label="Blank cells should erase data in existing contacts"
                  />
                  <Switch
                    fullWidth
                    margin="none"
                    checked={hasPermission}
                    onChange={this.onCheck}
                    name="hasPermission"
                    label={t(
                      'import-contact-permission-check',
                      'I have obtained explicit permission to send email and/or text messages to these contacts.',
                    )}
                  />
                  <Box textAlign="right" mt={2}>
                    <Button
                      disabled={!file || (!hasAll && !groups.length)}
                      onClick={this.onSubmit}
                    >
                      {t('process-file', 'Process File')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Paper>
      </PageWrapper>
    );
  }
}

export default connect((state) => ({
  ...state.settings,
  user: state.user.user,
}))(withTranslation('settings')(Import));
